import { PointerEvent, RefObject, useRef } from 'react'
import { ChevronsUpDown } from 'lucide-react'
import { VisualCrop } from 'app/frontend/types/custom/VisualPlan'
import { cn } from '@/lib/utils'
import { MIN_CROP_HEIGHT } from './constants'

type Props = {
  crop: VisualCrop | undefined
  fullHeight: number
  containerRef: RefObject<HTMLDivElement | null>
  reservingSpace: boolean
  onCropChange: (crop?: VisualCrop) => void
  onResizeDraggingChange: (dragging: boolean) => void
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const ScreenCropControls = ({
  crop,
  fullHeight,
  containerRef,
  reservingSpace,
  onCropChange,
  onResizeDraggingChange,
}: Props) => {
  const cropHeight = crop && crop.height < 1 ? crop.height : 1
  const resizeRef = useRef<{
    originTop: number
    originFullHeight: number
  } | null>(null)
  const panRef = useRef<{
    clientY: number
    originY: number
    height: number
  } | null>(null)

  const applyHeightFromPointer = (clientY: number) => {
    if (!resizeRef.current) return
    const nextHeight = clamp(
      (clientY - resizeRef.current.originTop) /
        resizeRef.current.originFullHeight,
      MIN_CROP_HEIGHT,
      1,
    )
    if (nextHeight >= 1) {
      onCropChange(undefined)
      return
    }
    onCropChange({
      height: nextHeight,
      y: crop?.y ?? 0,
    })
  }

  const handleResizePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect || fullHeight <= 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    onResizeDraggingChange(true)
    resizeRef.current = {
      originTop: rect.top,
      originFullHeight: fullHeight,
    }
    applyHeightFromPointer(event.clientY)
  }

  const handleResizePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!resizeRef.current) return
    applyHeightFromPointer(event.clientY)
  }

  const handleResizePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!resizeRef.current) return
    applyHeightFromPointer(event.clientY)
    resizeRef.current = null
    onResizeDraggingChange(false)
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const applyPanFromPointer = (clientY: number) => {
    if (!panRef.current || fullHeight <= 0) return
    const travel = 1 - panRef.current.height
    if (travel <= 0) return
    const dy = (clientY - panRef.current.clientY) / (fullHeight * travel)
    onCropChange({
      height: panRef.current.height,
      y: clamp(panRef.current.originY + dy, 0, 1),
    })
  }

  const handlePanPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (cropHeight >= 1) return
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    panRef.current = {
      clientY: event.clientY,
      originY: crop?.y ?? 0,
      height: cropHeight,
    }
  }

  const handlePanPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!panRef.current) return
    applyPanFromPointer(event.clientY)
  }

  const handlePanPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!panRef.current) return
    applyPanFromPointer(event.clientY)
    panRef.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  return (
    <>
      <div
        className={cn(
          'absolute inset-x-0 z-20 flex h-3 cursor-ns-resize touch-none items-end justify-center',
          !reservingSpace && 'bottom-0',
        )}
        style={
          reservingSpace
            ? { top: `${cropHeight * 100}%`, transform: 'translateY(-100%)' }
            : undefined
        }
        onPointerDown={handleResizePointerDown}
        onPointerMove={handleResizePointerMove}
        onPointerUp={handleResizePointerUp}
        onPointerCancel={handleResizePointerUp}
      >
        <div className='mb-0.5 h-1 w-16 rounded-full bg-border' />
      </div>
      {cropHeight < 1 ? (
        <div
          className={cn(
            'absolute right-2 z-20 flex h-8 w-8 -translate-y-1/2 cursor-ns-resize touch-none items-center justify-center rounded-full border border-border bg-card/95 shadow',
            'active:cursor-grabbing',
            !reservingSpace && 'top-1/2',
          )}
          style={reservingSpace ? { top: `${cropHeight * 50}%` } : undefined}
          onPointerDown={handlePanPointerDown}
          onPointerMove={handlePanPointerMove}
          onPointerUp={handlePanPointerUp}
          onPointerCancel={handlePanPointerUp}
        >
          <ChevronsUpDown className='h-4 w-4' />
        </div>
      ) : null}
    </>
  )
}

export default ScreenCropControls
